import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { Button } from '@/components/Button/Button';
import { MODAL_TIMEOUT } from '@/config/constants';
import { AnalyticsEvent, useAnalytics } from '@/hooks/useAnalytics';
import { useSocket } from '@/hooks/useSocket';
import { useWidgetState } from '@/hooks/useWidgetState';
import { shouldDisplaySurveySelector, chatModalState, afterSurveyAction, } from '@/state/session';
import { webChatStateState } from '@/state/socket';
import { sleep } from '@/util/utils';
import styles from './CloseChat.module.scss';
/**
 * Shows a chat form when the chatmodal is set to `closing`, `inactivity` or `expired`.
 */
export const CloseChat = ({ type }) => {
    const { t } = useTranslation();
    const { closeSession } = useSocket();
    const { closeWidget } = useWidgetState();
    const setWebChatState = useSetRecoilState(webChatStateState);
    const { trackEvent } = useAnalytics();
    const shouldDisplaySurvey = useRecoilValue(shouldDisplaySurveySelector);
    const setChatModal = useSetRecoilState(chatModalState);
    const setAfterSurveyAction = useSetRecoilState(afterSurveyAction);
    /**
     * Modal config map with type-specific values
     */
    const modalConfigs = useMemo(() => ({
        ["closing" /* ChatModal.Closing */]: {
            title: t('modal.end_old'),
            primaryButton: t('buttons.end_chat'),
            secondaryButton: t('buttons.cancel'),
            primaryEventTracker: AnalyticsEvent.PopoverEndChatClicked,
            secondaryEventTracker: AnalyticsEvent.PopoverEndChatContinueClicked,
        },
        ["expired" /* ChatModal.Expired */]: {
            title: t('modal.expired'),
            primaryButton: t('buttons.start_new_chat'),
            secondaryButton: t('buttons.close'),
            primaryEventTracker: AnalyticsEvent.PopoverExpiredNewClicked,
            secondaryEventTracker: AnalyticsEvent.PopoverExpiredEndClicked,
        },
        ["inactivity" /* ChatModal.Inactivity */]: {
            title: t('modal.inactivity'),
            primaryButton: t('buttons.start_new_chat'),
            secondaryButton: t('buttons.continue'),
            primaryEventTracker: AnalyticsEvent.PopoverInactivityEndChatClicked,
            secondaryEventTracker: AnalyticsEvent.PopoverInactivityContinueClicked,
        },
        // Add fallbacks for other ChatModal types to prevent TypeScript errors
        ["survey" /* ChatModal.Survey */]: {
            title: '',
            primaryButton: '',
            secondaryButton: '',
        },
        ["survey-full" /* ChatModal.SurveyFull */]: {
            title: '',
            primaryButton: '',
            secondaryButton: '',
        },
        ["closed" /* ChatModal.Resolved */]: {
            title: '',
            primaryButton: '',
            secondaryButton: '',
        },
    }), [t]);
    const config = useMemo(() => modalConfigs[type], [modalConfigs, type]);
    /**
     * Primary button click handler
     */
    const handlePrimaryClick = useCallback(async () => {
        if (config.primaryEventTracker) {
            trackEvent(config.primaryEventTracker);
        }
        if (type === "inactivity" /* ChatModal.Inactivity */) {
            // For inactivity in v1, primary button means "Start New Chat"
            closeSession();
            setChatModal(null);
            return;
        }
        const clickAction = () => {
            // cleaning last session
            closeSession();
            // If the session was expired, and the user selects to start a new one, we need to
            // reinitiate the conversation
            if (type === "expired" /* ChatModal.Expired */) {
                setWebChatState("init" /* WebChatState.Init */);
            }
        };
        setChatModal(null);
        if (shouldDisplaySurvey) {
            await sleep(MODAL_TIMEOUT);
            setAfterSurveyAction(() => clickAction);
            setChatModal("survey" /* ChatModal.Survey */);
        }
        else {
            clickAction();
        }
    }, [
        closeSession,
        config.primaryEventTracker,
        setChatModal,
        setAfterSurveyAction,
        setWebChatState,
        shouldDisplaySurvey,
        trackEvent,
        type,
    ]);
    /**
     * Secondary button click handler
     */
    const handleSecondaryClick = useCallback(async () => {
        if (config.secondaryEventTracker) {
            trackEvent(config.secondaryEventTracker);
        }
        if (type === "expired" /* ChatModal.Expired */) {
            setChatModal(null);
            const clickAction = () => {
                closeSession();
                closeWidget();
            };
            if (shouldDisplaySurvey) {
                await sleep(MODAL_TIMEOUT);
                setAfterSurveyAction(() => clickAction);
                setChatModal("survey" /* ChatModal.Survey */);
            }
            else {
                clickAction();
            }
        }
        else {
            // For other types (like Closing), just close the modal
            setChatModal(null);
        }
    }, [
        closeSession,
        closeWidget,
        config.secondaryEventTracker,
        setAfterSurveyAction,
        setChatModal,
        shouldDisplaySurvey,
        trackEvent,
        type,
    ]);
    return (_jsxs("div", { className: styles.form, children: [_jsx("div", { className: styles.title, children: config.title }), _jsxs("div", { className: styles.buttonContainer, children: [_jsx(Button, { className: "end-btn" /* TestClassName.EndSessionButton */, onClick: handlePrimaryClick, children: config.primaryButton }), _jsx(Button, { onClick: handleSecondaryClick, variant: "secondary", children: config.secondaryButton })] })] }));
};
